import React, {useMemo, useState} from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {parseMonthDay, validateMonthDay} from '@/utils/auditPeriodSplit';

/** Step 2: user confirms financial year-end dd/MM */
export type FirstAuditConfirmResult = {yearEndMD: string};

type Props = {
    open: boolean;
    onConfirm: (result: FirstAuditConfirmResult) => void;
    /** Optional: allow closing without confirm only when parent clears period */
    onDismiss?: () => void;
};

/**
 * Step 2 (periodFrom === inCorpDate && cessation): confirm year-end day/month.
 * Former 18-month Yes/No dialog removed.
 */
export default function FirstAuditPeriodConfirmDialog({open, onConfirm, onDismiss}: Props) {
    const [mdInput, setMdInput] = useState('');
    const [mdError, setMdError] = useState<string | null>(null);

    React.useEffect(() => {
        if (open) {
            setMdInput('');
            setMdError(null);
        }
    }, [open]);

    const parsed = useMemo(() => parseMonthDay(mdInput), [mdInput]);

    const handleConfirmYearEnd = () => {
        const trimmed = mdInput.trim();
        const m = /^([0-9]{1,2})\/([0-9]{1,2})$/.exec(trimmed);
        if (!m) {
            setMdError('Please enter day/month as dd/MM (e.g. 31/03)');
            return;
        }
        const day = Number(m[1]);
        const month = Number(m[2]);
        if (!validateMonthDay(day, month)) {
            setMdError(
                month === 2 && day === 29
                    ? '29/02 is not allowed. Please use another day/month.'
                    : 'Invalid day/month. Please enter a valid dd/MM.',
            );
            return;
        }
        const normalized = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`;
        onConfirm({yearEndMD: normalized});
    };

    const formatMdAsTyped = (raw: string) => {
        const digits = raw.replace(/[^\d]/g, '').slice(0, 4);
        if (digits.length <= 2) return digits;
        return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    };

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                onDismiss?.();
            }}
            maxWidth="xs"
            fullWidth
            disableEscapeKeyDown
        >
            <DialogTitle>Please confirm the period end date!</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{pt: 1}}>
                    <Typography variant="body2" color="text.secondary">
                        Enter the financial year-end day and month (dd/MM), without year.
                    </Typography>
                    <TextField
                        label="Year end (dd/MM)"
                        placeholder="31/03"
                        value={mdInput}
                        onChange={(e) => {
                            setMdInput(formatMdAsTyped(e.target.value));
                            setMdError(null);
                        }}
                        error={!!mdError}
                        helperText={mdError || (parsed ? undefined : 'Example: 31/03')}
                        fullWidth
                        size="small"
                        autoFocus
                        inputProps={{maxLength: 5, inputMode: 'numeric'}}
                    />
                    {mdError && (
                        <Alert severity="error" variant="outlined">
                            {mdError}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={handleConfirmYearEnd} variant="contained" color="primary">
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
}
